import React, { useEffect, useState } from 'react';
import withRouter from '../../../components/withRouter';
import TableContainer from '../../../components/TableContainerReactTable';
import { deletePlacement, getPlacements } from '../services/placementApi';
import { Placement } from '../types/placement';
import { toast } from 'react-toastify';
import { useProfile } from '../../(identity)/hooks/UserHooks';
import AddEditPlacementModal from '../components/placement/AddEditPlacementModal';
import ImportPlacementModal from '../components/placement/ImportPlacementModal';
import numeral from 'numeral';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, ExternalLink, Pencil, Trash2, Image, Plus } from 'lucide-react';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

const Index = (props: any) => {
    const { userProfile } = useProfile();
    const [data, setData] = useState<Placement[]>([]);
    const [dataToEdit, setDataToEdit] = useState<Placement | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [isAddEditModalOpen, setIsAddEditModalOpen] = useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState<boolean>(false);
    numeral.defaultFormat('0b');

    useEffect(() => {
        getPlacements().then((res) => {
            setData(res);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    const handleUpdateData = (newData: Placement) => {
        const updatedData = data.map((item) => {
            if (item.id === newData.id) {
                return newData;
            }
            return item;
        });
        setData(updatedData);
    };

    const handleAddData = (newData: Placement | Placement[]) => {
        if (Array.isArray(newData)) {
            setData([...data, ...newData]);
        } else {
            setData([...data, newData]);
        }
    };

    const handleRemoveData = (id: number) => {
        const updatedData = [...data].filter((item: any) => item.id !== id);
        setData(updatedData);
    };

    const toggleAddEditModal = () => {
        setIsAddEditModalOpen(!isAddEditModalOpen);
    };

    const toggleImportModal = () => {
        setIsImportModalOpen(!isImportModalOpen);
    };

    const handleDelete = (id: number) => {
        if (window.confirm('Tem certeza que deseja excluir este placement?')) {
            if (window.confirm('Esta ação é irreversível. Deseja continuar?')) {
                setLoading(true);
                deletePlacement(id)
                    .then(() => {
                        handleRemoveData(id);
                        toast.success('Placement excluído com sucesso');
                    })
                    .catch(() => {
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    };

    const columns: any[] = [
        {
            header: 'Midia Cad',
            cell: (cell: any) => <span className="font-semibold">{cell.getValue()}</span>,
            accessorKey: 'publisher.mediaCad',
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: 'Nome do placement',
            accessorKey: 'name',
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tipo',
            accessorKey: 'type',
            cell: (cell: any) => {
                const tipo = cell.getValue();
                let badgeClass = 'bg-primary text-primary';
                if (tipo === 'display') badgeClass = 'bg-green-600 text-white';
                else if (tipo === 'video') badgeClass = 'bg-yellow-600 text-white';
                else if (tipo === 'audio') badgeClass = 'bg-blue-600 text-white';
                return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>{tipo}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes',
            accessorKey: 'sizes',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell: any[] = [];
                row.sizes?.forEach((size: string) => {
                    actionCell.push(
                        <span key={size} className="px-2 py-1 rounded-full text-xs border border-primary text-primary">{size}</span>
                    );
                });
                return <div className="flex gap-2 flex-wrap">{actionCell}</div>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Sizes Expandidos',
            accessorKey: 'sizesExpandidos',
            cell: (cell: any) => {
                const row = cell.row.original;
                if (row.sizesExpandidos && row.sizesExpandidos.length > 0) {
                    const actionCell: any[] = [];
                    row.sizesExpandidos?.forEach((sizeExpandido: string) => {
                        actionCell.push(
                            <span key={sizeExpandido} className="px-2 py-1 rounded-full text-xs border border-secondary text-secondary">
                                {sizeExpandido}
                            </span>
                        );
                    });
                    return <div className="flex gap-2 flex-wrap">{actionCell}</div>;
                }
                return '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Rich Media',
            accessorKey: 'maxRichMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxRichMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Imagem',
            accessorKey: 'maxImageSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxImageSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Aceita Audio/Video',
            accessorKey: 'acceptsAudioVideo',
            cell: (cell: any) => {
                const row = cell.row.original;
                const className = row.acceptsAudioVideo ? 'bg-green-600 text-white' : 'bg-red-600 text-white';
                return <span className={`px-2 py-1 rounded-full text-xs font-medium ${className}`}>{row.acceptsAudioVideo ? 'sim' : 'não'}</span>;
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Peso Máximo para Vídeo',
            accessorKey: 'maxMediaSizeInKb',
            cell: (cell: any) => {
                const row = cell.row.original;
                return numeral(row.maxMediaSizeInKb * 1000).format() || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Tempo máximo de vídeo/audio (s)',
            accessorKey: 'maxMediaDurationInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.maxMediaDurationInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Recarregamento (s)',
            accessorKey: 'refreshInSeconds',
            cell: (cell: any) => {
                const row = cell.row.original;
                return row.refreshInSeconds || '-';
            },
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Status',
            accessorFn: (row: any) => (row.status === 1 ? 'ativo' : 'inativo'),
            enableSorting: true,
            enableColumnFilter: false,
        },
        {
            header: 'Actions',
            cell: (cell: any) => {
                const row = cell.row.original;
                const actionCell: any[] = [];

                if (row.mediaKitUrl || row.mediaKitFile) {
                    actionCell.push(
                        <TooltipProvider key="mediaKit">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <a
                                        href={row.mediaKitUrl || row.mediaKitFile}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
                                    >
                                        <Image className="h-4 w-4" />
                                    </a>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Mídia Kit</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(
                        <TooltipProvider key="edit">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white shadow hover:bg-green-700 h-9 px-4 py-2"
                                        onClick={() => {
                                            setDataToEdit(row);
                                            toggleAddEditModal();
                                        }}
                                    >
                                        <Pencil className="h-4 w-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Editar</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                if (userProfile?.role === 'ROLE_SUPER_ADMIN') {
                    actionCell.push(
                        <TooltipProvider key="delete">
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-red-600 text-white shadow hover:bg-red-700 h-9 px-4 py-2"
                                        onClick={() => handleDelete(row.id)}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Excluir</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    );
                }

                return <div className="flex gap-2">{actionCell}</div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        },
    ];

    if (userProfile?.role !== 'ROLE_VEICULO_ADMIN') {
        columns.splice(1, 0, {
            header: 'Publisher',
            accessorKey: 'publisher.name',
            enableColumnFilter: false,
            enableSorting: true,
        });
    }

    document.title = 'Blocos de Anúncios | Ad Pulse';
    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Blocos de Anúncios</CardTitle>
                                        <div className="flex gap-2">
                                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                                <Button
                                                    onClick={() => {
                                                        setDataToEdit(null);
                                                        toggleAddEditModal();
                                                    }}
                                                    className="flex items-center gap-2"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                    Adicionar placements
                                                </Button>
                                            )}

                                            {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                                <Button
                                                    onClick={toggleImportModal}
                                                    variant="outline"
                                                    className="flex items-center gap-2"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                    Importar placements
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <React.Fragment>
                                        <div className="table-responsive">
                                            {!loading ? (
                                                <TableContainer
                                                    columns={(columns || [])}
                                                    data={(data || [])}
                                                    isGlobalFilter={true}
                                                    customPageSize={30}
                                                    SearchPlaceholder="Procurar..."
                                                />
                                            ) : (
                                                <div className="text-center">
                                                    <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>

                                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                        <AddEditPlacementModal
                                            placement={dataToEdit}
                                            isOpen={isAddEditModalOpen}
                                            toggle={toggleAddEditModal}
                                            onAddData={handleAddData}
                                            onUpdateData={handleUpdateData}
                                        />
                                    )}
                                    {userProfile?.role === 'ROLE_SUPER_ADMIN' && (
                                        <ImportPlacementModal
                                            isOpen={isImportModalOpen}
                                            toggle={toggleImportModal}
                                            onAddData={handleAddData}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default withRouter(Index);
